import {
  readStudioGenerationAuthority,
} from '@/features/generation-authority/generation-authority-readback'
import {
  normalizeStudioDesignOntologyBinding,
  normalizeStudioDesignSystemRelease,
  type StudioProjectPlan,
} from '@/features/project/lib/studio-project-plan'
import { readProjectGenerationMarker } from '@/features/project/lib/project-generation-durability'
import {
  readGeneratedArtifactPersistenceSettings,
  type StoredGeneratedArtifactCommit,
} from '@/features/generated-artifact/authenticated-studio-generated-artifact-persistence'
import { getCatalog } from '@/features/shared/services/block-loader'
import {
  readStudioGenerationAuthorityAppSpecSnapshot,
  type ProjectMeta,
  type StudioProject,
} from '@/services/project-api'
import requiredUiBlockGenerationExamples from '../../../../../../packages/cogo-schema/ui-block-source-corpus/required-library/required-ui-block-generation-examples.v1.json'

export interface GeneratedArtifactIdentity {
  persistence: 'saved' | 'uncommitted-local' | 'browser-local' | 'session-only'
  artifactId: string | null
  contentHash: string | null
  requestId: string | null
  runId: string | null
  ownerBotProfileId: string | null
  revision: string | null
  currentProjectRevision: string | null
  evidenceRefs: Array<{ ref: string; contentHash: string }>
  traceId: string | null
  appSpecRef: string | null
  designSystem: {
    releaseId: string
    packageLabel: string
    snapshotHash: string
    bindingHash: string
  } | null
  theme: {
    style: string | null
    primaryColor: string | null
    fontFamily: string | null
  } | null
  tokenRefs: string[]
  uiBlockRefs: string[]
}

interface GeneratedArtifactIdentityInput {
  projectId: string | null
  projectSource: string | null
  projectMeta: ProjectMeta | null
  projectSettings: Record<string, unknown> | null
  projectPlan: StudioProjectPlan | null
  project: unknown
  isDirty: boolean
  hasSaveConflict: boolean
  hasUncommittedArtifactLineage?: boolean
}

function record(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null
}

function nonEmptyString(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value : null
}

function sortedUnique(values: Array<string | null | undefined>): string[] {
  return [...new Set(values.filter((value): value is string => Boolean(value)))].sort()
}

const REQUIRED_UI_BLOCK_EXAMPLE_PREFIX = 'required-ui-block-example:'
const SHA256_REF = /^sha256:[0-9a-f]{64}$/

function readCurrentRequiredUiBlockArtifactHashes(value: unknown): ReadonlyMap<string, string> {
  const manifest = record(value)
  if (
    manifest?.schemaVersion !== 'cogo.required-ui-block-generation-examples.v1' ||
    !Array.isArray(manifest.patterns)
  ) return new Map()

  const hashes = new Map<string, string>()
  const conflicted = new Set<string>()
  for (const pattern of manifest.patterns) {
    const examples = record(pattern)?.examples
    if (!Array.isArray(examples)) continue
    for (const example of examples) {
      const exampleRecord = record(example)
      const exampleId = nonEmptyString(exampleRecord?.exampleId)
      const artifactHash = nonEmptyString(exampleRecord?.artifactHash)
      if (!exampleId || !artifactHash || !SHA256_REF.test(artifactHash)) continue
      const existing = hashes.get(exampleId)
      if (existing && existing !== artifactHash) {
        hashes.delete(exampleId)
        conflicted.add(exampleId)
      } else if (!conflicted.has(exampleId)) {
        hashes.set(exampleId, artifactHash)
      }
    }
  }
  return hashes
}

const CURRENT_REQUIRED_UI_BLOCK_ARTIFACT_HASHES =
  readCurrentRequiredUiBlockArtifactHashes(requiredUiBlockGenerationExamples)

function readSourceExampleRefs(
  project: unknown,
  receiptBoundCanonicalArtifact: Record<string, unknown>,
): string[] {
  const projectRecord = record(project)
  const blocks = record(projectRecord?.blocks)
  if (!blocks) return []
  const sourceAuthorities = record(receiptBoundCanonicalArtifact.sourceAuthority)
  const manifest = record(projectRecord?.manifest)
  const pages = Array.isArray(manifest?.pages) ? manifest.pages : []
  const reachableBlockNames = new Set(
    pages.flatMap((page) => {
      const pageBlocks = record(page)?.blocks
      return Array.isArray(pageBlocks)
        ? pageBlocks.filter((name): name is string => typeof name === 'string' && name.length > 0)
        : []
    }),
  )
  const catalogBlocks = getCatalog().categories.flatMap((category) =>
    category.blocks.map((block) => ({ category: category.id, id: block.id })),
  )
  return sortedUnique(
    Object.entries(blocks).map(([blockName, block]) => {
      if (!reachableBlockNames.has(blockName)) return null
      const blockRecord = record(block)
      const sourceExampleId = nonEmptyString(blockRecord?.sourceExampleId) ??
        nonEmptyString(record(blockRecord?.meta)?.sourceExampleId) ??
        nonEmptyString(record(blockRecord?.metadata)?.sourceExampleId)
      if (!sourceExampleId) return null
      const categoryHint = nonEmptyString(blockRecord?.sourceExampleCategory) ??
        nonEmptyString(blockRecord?.category) ??
        nonEmptyString(record(blockRecord?.meta)?.category) ??
        nonEmptyString(record(blockRecord?.metadata)?.category)
      const requiredExampleRef = sourceExampleId.startsWith(REQUIRED_UI_BLOCK_EXAMPLE_PREFIX)
      const catalogRef = requiredExampleRef
        ? sourceExampleId.slice(REQUIRED_UI_BLOCK_EXAMPLE_PREFIX.length)
        : sourceExampleId
      const slash = catalogRef.indexOf('/')
      if (
        requiredExampleRef &&
        (slash <= 0 || slash !== catalogRef.lastIndexOf('/') || slash === catalogRef.length - 1)
      ) return null
      const refCategory = slash > 0 ? catalogRef.slice(0, slash) : categoryHint
      const refId = slash > 0 ? catalogRef.slice(slash + 1) : catalogRef
      const matches = catalogBlocks.filter((entry) =>
        entry.id === refId && (!refCategory || entry.category === refCategory),
      )
      if (matches.length !== 1) return null
      if (requiredExampleRef) {
        const sourceAuthority = record(sourceAuthorities?.[blockName])
        const currentArtifactHash = CURRENT_REQUIRED_UI_BLOCK_ARTIFACT_HASHES.get(sourceExampleId)
        if (
          !currentArtifactHash ||
          nonEmptyString(sourceAuthority?.sourceExampleId) !== sourceExampleId ||
          nonEmptyString(sourceAuthority?.artifactHash) !== currentArtifactHash
        ) return null
      }
      return sourceExampleId
    }),
  )
}

function readTokenRefs(appSpec: unknown): string[] {
  const spec = record(appSpec)
  const artifact = record(spec?.designBoundApplicationArtifact)
  const design = record(artifact?.design)
  const nodes = Array.isArray(design?.nodes) ? design.nodes : []
  const nodeRefs = nodes.flatMap((node) => {
    const refs = record(node)?.tokenRefs
    return Array.isArray(refs) ? refs.map(nonEmptyString) : []
  })
  const explicitTokens = record(spec?.designSystemTokens)
  return sortedUnique([
    ...nodeRefs,
    ...(explicitTokens ? Object.keys(explicitTokens) : []),
  ])
}

function readCurrentDurableCommit(input: {
  projectId: string
  projectRevision: string
  settings: Record<string, unknown> | null
}): StoredGeneratedArtifactCommit | null {
  try {
    const binding = readGeneratedArtifactPersistenceSettings(input.settings)
    if (!binding) return null
    const candidates = binding.commits.filter((commit) =>
      commit.persistenceReceipt.projectId === input.projectId &&
      commit.projectRevision.value === input.projectRevision &&
      commit.persistenceReceipt.revision.value === input.projectRevision,
    )
    // A project revision names one authority-issued block replacement. More
    // than one receipt claiming it is ambiguous and must not become lineage.
    return candidates.length === 1 ? candidates[0] : null
  } catch {
    // Present-but-malformed durable settings fail closed into local status.
    return null
  }
}

/**
 * Build the cross-surface identity from persisted receipts and the loaded
 * artifact only. This deliberately does not infer a release from tokens, a UI
 * Block from a block name, or a revision from local edit counters.
 */
export function buildGeneratedArtifactIdentity(
  input: GeneratedArtifactIdentityInput,
): GeneratedArtifactIdentity {
  const serverBacked = input.projectSource === 'server' && Boolean(input.projectId)
  const currentProjectRevision = serverBacked
    ? input.projectMeta?.projectRevision?.value ?? null
    : null
  const durableCommit = input.projectId && currentProjectRevision
    ? readCurrentDurableCommit({
        projectId: input.projectId,
        projectRevision: currentProjectRevision,
        settings: input.projectSettings,
      })
    : null
  const saved = Boolean(
    serverBacked &&
    durableCommit &&
    !input.isDirty &&
    !input.hasSaveConflict &&
    !input.hasUncommittedArtifactLineage,
  )
  const persistence: GeneratedArtifactIdentity['persistence'] = saved
    ? 'saved'
    : serverBacked
      ? 'uncommitted-local'
      : input.projectSource === 'import'
        ? 'browser-local'
        : 'session-only'
  const revision = saved ? durableCommit!.projectRevision.value : null
  const marker = saved ? readProjectGenerationMarker(input.projectSettings) : null

  const authority = saved && input.projectId
    ? readStudioGenerationAuthority({
        id: input.projectId,
        settings: input.projectSettings,
      } as StudioProject)
    : null
  const finalAuthority = authority?.status === 'available'
    ? authority.envelope
    : null

  let appSpec: unknown = null
  if (input.projectId && input.projectSettings && finalAuthority?.applicationAuthority.appSpecRef) {
    try {
      appSpec = readStudioGenerationAuthorityAppSpecSnapshot(
        input.projectSettings,
        input.projectId,
        finalAuthority.applicationAuthority.appSpecRef,
      ).appSpec
    } catch {
      // Present-but-invalid evidence is intentionally not projected into UI.
      appSpec = null
    }
  }

  const release = saved ? normalizeStudioDesignSystemRelease(
    input.projectPlan?.designSystemRelease,
  ) : null
  const binding = saved ? normalizeStudioDesignOntologyBinding(
    input.projectPlan?.designOntologyBinding,
  ) : null
  const boundArtifact = record(record(appSpec)?.designBoundApplicationArtifact)
  const designSystem = release && binding && boundArtifact &&
    binding.designOntologyReleaseRef === release.releaseId &&
    boundArtifact.designOntologyReleaseRef === release.releaseId &&
    boundArtifact.applicationSemanticHash === binding.applicationSemanticHash &&
    boundArtifact.designBindingHash === binding.designBindingHash &&
    boundArtifact.renderContractHash === binding.renderContractHash
    ? {
        releaseId: release.releaseId,
        packageLabel: `${release.packageId}@${release.packageVersion}`,
        snapshotHash: release.packageSnapshotHash,
        bindingHash: binding.designBindingHash,
      }
    : null

  const themeHint = record(record(appSpec)?.themeHint)
  const theme = appSpec && themeHint
    ? {
        style: nonEmptyString(themeHint.style),
        primaryColor: nonEmptyString(themeHint.primaryColor),
        fontFamily: nonEmptyString(themeHint.fontFamily),
      }
    : null

  return {
    persistence,
    artifactId: saved ? durableCommit!.artifactId : null,
    contentHash: saved ? durableCommit!.contentHash : null,
    requestId: saved ? durableCommit!.requestId : null,
    runId: saved ? durableCommit!.runId : null,
    ownerBotProfileId: saved
      ? durableCommit!.persistenceReceipt.ownerBotProfileId
      : null,
    revision,
    currentProjectRevision,
    evidenceRefs: saved
      ? durableCommit!.evidenceRefs.map((ref) => ({ ...ref }))
      : [],
    traceId: marker?.traceId ?? null,
    appSpecRef: finalAuthority?.applicationAuthority.appSpecRef ?? null,
    designSystem,
    theme,
    tokenRefs: appSpec ? readTokenRefs(appSpec) : [],
    uiBlockRefs: saved
      ? readSourceExampleRefs(input.project, durableCommit!.canonicalCogoJson)
      : [],
  }
}
